/**
 * Shared formatting utilities for the lending application
 */

/**
 * Format amount as Indian Rupee currency
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Compact INR for KPI cards (lakhs / crores).
 * Examples: ₹1.50 L, ₹2.35 Cr
 */
export const formatCurrencyCompact = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '₹0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(2)} L`;
  return `${sign}${formatCurrency(abs)}`;
};

/**
 * Format date in Indian locale (DD MMM YYYY)
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

/**
 * Format date as ISO string (YYYY-MM-DD)
 */
export const formatDateISO = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
};

/**
 * Format percentage
 */
export const formatPercent = (value, decimals = 2) => {
  if (!value && value !== 0) return '-';
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format number with Indian numbering system
 */
export const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return new Intl.NumberFormat('en-IN').format(num);
};

/**
 * Calculate EMI using standard formula
 */
export const calculateEMI = (principal, annualRate, tenureMonths) => {
  const p = parseFloat(principal) || 0;
  const r = (parseFloat(annualRate) || 0) / 12 / 100;
  const n = parseInt(tenureMonths) || 0;
  
  if (!p || !r || !n) return 0;
  
  const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return emi;
};

/**
 * Split total months into years and remaining months
 */
export const splitTenure = (totalMonths) => {
  const months = parseInt(totalMonths) || 0;
  return {
    years: Math.floor(months / 12),
    months: months % 12,
  };
};

/**
 * Combine years and months into total months
 */
export const combineTenure = (years, months) => {
  return ((parseInt(years) || 0) * 12) + (parseInt(months) || 0);
};

/**
 * Format tenure months as "X Years Y Months" display string
 */
export const formatTenure = (totalMonths) => {
  const { years, months } = splitTenure(totalMonths);
  const parts = [];
  if (years > 0) parts.push(`${years} Year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} Month${months !== 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(' ') : '0 Months';
};

/**
 * Normalize status string for case-insensitive comparison
 */
export const normalizeStatus = (status) => {
  return (status || '').toLowerCase().trim();
};

/**
 * Format date and time
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format amount as INR with decimals, returns '-' for missing values.
 * Used by data management pages (demands, transfers, etc.)
 */
export const formatINR = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
};

/**
 * Format date as 'dd MMM yyyy' using date-fns.
 * Returns '-' for missing values.
 */
export const formatDateDMY = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

/** Persist IFSC + account in the existing encrypted bank_account column. */
export const encodeBankAccount = (ifsc, account) => {
  const i = (ifsc || '').trim().toUpperCase();
  const a = (account || '').trim();
  if (i && a) return `${i}|${a}`;
  return a || i || undefined;
};

export const decodeBankAccount = (raw) => {
  if (!raw) return { ifsc: '', account: '' };
  const text = String(raw).trim();
  if (text.includes('|')) {
    const [ifsc, ...rest] = text.split('|');
    return { ifsc: ifsc.trim().toUpperCase(), account: rest.join('|').trim() };
  }
  // Match IFSC (4 letters + 0 + 6 alphanumeric) followed by separator(s) and account.
  // Uses [^\s/] to prevent backtracking between separator and account.
  const match = /^([A-Za-z]{4}0[A-Za-z0-9]{6})[\s/]+([^\s/].*)$/.exec(text);
  if (match) return { ifsc: match[1].toUpperCase(), account: match[2].trim() };
  return { ifsc: '', account: text };
};
